import React from 'react';
import { Mention, MentionsInput } from 'react-mentions';
import { Box, Tooltip } from 'pounceV2';
import { useTheme, Theme } from '@mui/material';

import type { InvokableAISkills } from 'Pages/AI/graphql/invokableAISkills.generated';
import { SKILL_MENTION_MARKUP, SKILL_MENTION_TRIGGER } from './constants';

const LAYER_BASE_STYLE = {
  padding: '16px 12px 8px',
  minHeight: 40,
  maxHeight: 240,
  boxSizing: 'border-box',
  border: '0 solid transparent',
  fontFamily: 'inherit',
  fontSize: 14,
  lineHeight: 1.5,
  letterSpacing: 'normal',
} as const;

type ThemeTokens = Theme['palette']['tokens'];

const getMentionsInputStyle = (tokens: ThemeTokens) => ({
  control: { fontSize: 14, minHeight: 40 },
  '&multiLine': {
    highlighter: {
      ...LAYER_BASE_STYLE,
      overflow: 'hidden auto',
      color: tokens.content.text.neutral[1],
      substring: { visibility: 'visible' },
    },
    input: {
      ...LAYER_BASE_STYLE,
      overflowY: 'auto',
      outline: 'none',
      color: 'transparent',
      caretColor: tokens.content.text.neutral[1],
      backgroundColor: 'transparent',
      resize: 'none',
    },
  },
  suggestions: {
    zIndex: 1400,
    borderRadius: '4px',
    backgroundColor: tokens.background.neutral.canvas.base,
    border: `1px solid ${tokens.stroke.neutral[4]}`,
    list: { fontSize: 14, maxHeight: 200, overflow: 'auto' },
    item: {
      padding: '6px 10px',
      color: tokens.content.text.neutral[1],
      '&focused': { backgroundColor: tokens.background.neutral.mid[3] },
    },
  },
});

type InvokableSkill = InvokableAISkills['invokableAISkills'][number];

// First sentence terminator followed by whitespace/EOS; skips intra-word
// terminators like "v1.2". "e.g. " is indistinguishable from a sentence end.
const FIRST_SENTENCE_RE = /^(?:[^.!?]|[.!?](?!\s|$))*[.!?](?=\s|$)/;

export interface MentionSuggestion {
  id: string;
  display: string;
  description?: string;
}

// Condense LLM-shaped descriptions (multi-sentence, leading "BEFORE …") into a single
// picker-friendly line. TODO: replace with a dedicated `displayDescription` field on the
// Skill backend model so authors write picker copy explicitly.
export function humanizeDescription(raw: string): string {
  const normalized = raw.replace(/\s+/g, ' ').trim();
  if (!normalized) {
    return '';
  }
  const sentenceMatch = normalized.match(FIRST_SENTENCE_RE);
  const firstSentence = sentenceMatch ? sentenceMatch[0] : normalized;
  return firstSentence.replace(/^BEFORE\s+(\S)/i, (_m, ch: string) => ch.toUpperCase());
}

export function toMentionSuggestions(skills: InvokableSkill[]): MentionSuggestion[] {
  return skills.map(s => ({
    id: s.name,
    display: s.displayName?.trim() || s.name,
    description: s.description ? humanizeDescription(s.description) : undefined,
  }));
}

export function filterSkills(suggestions: MentionSuggestion[], query: string): MentionSuggestion[] {
  const q = query.trim().toLowerCase();
  return q ? suggestions.filter(s => s.display.toLowerCase().includes(q)) : suggestions;
}

interface SkillMentionsFieldProps {
  value: string;
  onChange: (nextValue: string) => void;
  onSubmitShortcut: () => void;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  skills: InvokableSkill[];
}

const SkillMentionsFieldV3: React.FC<SkillMentionsFieldProps> = ({
  value,
  onChange,
  onSubmitShortcut,
  placeholder,
  autoFocus,
  disabled,
  skills,
}) => {
  const {
    palette: { tokens },
  } = useTheme();

  const skillSuggestions = React.useMemo(() => toMentionSuggestions(skills), [skills]);

  const filterSuggestions = React.useCallback(
    (query: string, callback: (results: MentionSuggestion[]) => void): void => {
      callback(filterSkills(skillSuggestions, query));
    },
    [skillSuggestions],
  );

  const renderSkillSuggestion = (
    suggestion: MentionSuggestion,
    _search: string,
    highlighted: React.ReactNode,
    _index: number,
    focused: boolean,
  ) => {
    const { description } = suggestion;
    if (!description) {
      return <Box truncated>{highlighted}</Box>;
    }
    // `focused` covers both arrow-key nav and mouse hover.
    return (
      <Tooltip content={description} placement="right" open={focused}>
        <Box truncated>{highlighted}</Box>
      </Tooltip>
    );
  };

  const mentionsInputStyle = React.useMemo(() => getMentionsInputStyle(tokens), [tokens]);
  const mentionStyle = React.useMemo(
    () => ({ color: tokens.content.text.accent[14][2] }),
    [tokens],
  );

  return (
    <MentionsInput
      value={value}
      onChange={(_ev: unknown, newValue: string) => onChange(newValue)}
      onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          onSubmitShortcut();
        }
      }}
      placeholder={placeholder}
      autoFocus={autoFocus}
      disabled={disabled}
      autoComplete="new-password"
      allowSuggestionsAboveCursor
      style={mentionsInputStyle}
      a11ySuggestionsListLabel="Skill suggestions"
      // Portal the dropdown so the wrapper's `overflow: hidden` doesn't clip it.
      suggestionsPortalHost={typeof document !== 'undefined' ? document.body : undefined}
    >
      <Mention
        trigger={SKILL_MENTION_TRIGGER}
        data={filterSuggestions}
        style={mentionStyle}
        displayTransform={(_id: string, display: string) => `${SKILL_MENTION_TRIGGER}${display}`}
        markup={SKILL_MENTION_MARKUP}
        appendSpaceOnAdd
        renderSuggestion={renderSkillSuggestion}
      />
    </MentionsInput>
  );
};

export default SkillMentionsFieldV3;
